import type {GetPostTeamInput} from '@perfectpost/perfect-post-common';
import type {NormalizedInterval} from 'date-fns';

/**
 * Clés de cache du domaine `team`.
 *
 * Voir `src/hooks/profile/keys.ts` pour le pourquoi de la fabrique.
 *
 * UNE SEULE ENTRÉE (côté Redux)
 *
 * `teamslice` ne chargeait qu'une chose — `fetchTeams`, qui rapporte les équipes
 * ET les fiches publiques de leurs membres — pour douze mutations. Comme pour
 * `draftslice`, l'essentiel du slice servait à recopier à la main le résultat de
 * ces mutations dans `state.team.teams`.
 *
 * Les publications et les classements d'une équipe, eux, n'ont jamais été dans
 * le store : les vues `team/*` les lisaient déjà avec `useQuery`, mais chacune
 * avec sa clé littérale. Elles sont rassemblées ici.
 */

/**
 * `GetPostTeamInput` dont `id` peut manquer : il vient de `useCurrentTeam()`,
 * donc `undefined` avant que les équipes soient chargées. La requête se
 * désactive elle-même tant qu'il est absent.
 */
export type TeamPostsQueryInput = Omit<GetPostTeamInput, 'id'> & {id: string | undefined};

/** Les `Date` sont sérialisées en ISO — cf. le commentaire de `profileKeys.view`. */
const serializePostsInput = (input: TeamPostsQueryInput) => ({
  id: input.id,
  begin: input.begin?.toISOString(),
  end: input.end?.toISOString(),
  member: input.member,
  sort: input.sort,
  order: input.order,
  limit: input.limit,
  offset: input.offset,
  favorite: input.favorite,
  medias: input.medias,
  categories: input.categories,
  objective: input.objective,
  search: input.search,
});

const serializeInterval = (interval: NormalizedInterval<Date>) =>
  [interval.start.toISOString(), interval.end.toISOString()] as const;

export const teamKeys = {
  all: ['team'] as const,

  /** Les équipes de l'utilisateur et leurs membres. */
  list: () => [...teamKeys.all, 'list'] as const,

  /** Toutes les listes de publications d'équipe, cible d'invalidation. */
  posts: () => [...teamKeys.all, 'posts'] as const,

  /** Une liste de publications : toute la requête entre dans la clé (cf. `organizationPostKeys.list`). */
  postList: (input: TeamPostsQueryInput) => [...teamKeys.posts(), serializePostsInput(input)] as const,

  /** `GET /team/:id/topcommenter` sur un intervalle. */
  topCommenter: (teamId: string, interval: NormalizedInterval<Date>) =>
    [...teamKeys.all, 'topCommenter', teamId, ...serializeInterval(interval)] as const,

  /** `GET /team/:id/topliker` sur un intervalle. */
  topLiker: (teamId: string, interval: NormalizedInterval<Date>) =>
    [...teamKeys.all, 'topLiker', teamId, ...serializeInterval(interval)] as const,

  mutations: () => [...teamKeys.all, 'mutation'] as const,
} as const;
